/**
 * Базовые примитивы интерфейса. Пишем свои поверх Tailwind-токенов вместо
 * генерации shadcn: компонентов нужно немного, а собственные варианты кнопок и
 * бейджей точнее ложатся на семантику домена (статусы разговора, тональность).
 */

import { clsx, type ClassValue } from 'clsx'
import type { ComponentProps, ReactNode } from 'react'
import { twMerge } from 'tailwind-merge'

export function cn(...inputs: ClassValue[]): string {
  return twMerge(clsx(inputs))
}

/* ---------- кнопка ---------- */

type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger' | 'accent'
type ButtonSize = 'sm' | 'md'

const buttonVariants: Record<ButtonVariant, string> = {
  primary: 'bg-brand-800 text-white hover:bg-brand-700 dark:bg-brand-600 dark:hover:bg-brand-500',
  secondary:
    'border border-line bg-surface text-ink hover:bg-surface-muted hover:border-line-strong',
  ghost: 'text-ink-muted hover:bg-surface-muted hover:text-ink',
  danger: 'border border-line bg-surface text-negative hover:bg-negative-soft',
  accent: 'bg-accent-500 text-white hover:bg-accent-600',
}

const buttonSizes: Record<ButtonSize, string> = {
  sm: 'h-8 px-3 text-[13px] gap-1.5',
  md: 'h-10 px-4 text-sm gap-2',
}

interface ButtonProps extends ComponentProps<'button'> {
  variant?: ButtonVariant
  size?: ButtonSize
  loading?: boolean
}

export function Button({
  variant = 'secondary',
  size = 'md',
  loading = false,
  className,
  disabled,
  children,
  ...props
}: ButtonProps) {
  return (
    <button
      // Курсор и блокировка на время запроса — иначе двойное нажатие создаёт
      // две сущности.
      className={cn(
        'inline-flex cursor-pointer items-center justify-center rounded-[var(--radius-control)] font-medium transition-colors duration-150',
        'disabled:pointer-events-none disabled:opacity-50',
        buttonVariants[variant],
        buttonSizes[size],
        className,
      )}
      disabled={disabled || loading}
      {...props}
    >
      {loading && <Spinner className="size-3.5" />}
      {children}
    </button>
  )
}

export function Spinner({ className }: { className?: string }) {
  return (
    <span
      aria-hidden="true"
      className={cn(
        'inline-block animate-spin rounded-full border-2 border-current border-t-transparent',
        className ?? 'size-4',
      )}
    />
  )
}

/* ---------- поверхности ---------- */

export function Card({ className, children, ...props }: ComponentProps<'section'>) {
  return (
    <section
      className={cn(
        'rounded-[var(--radius-card)] border border-line bg-surface shadow-[0_1px_2px_rgba(16,27,36,0.04)]',
        className,
      )}
      {...props}
    >
      {children}
    </section>
  )
}

export function CardHeader({
  title,
  description,
  actions,
}: {
  title: ReactNode
  description?: ReactNode
  actions?: ReactNode
}) {
  return (
    <header className="flex flex-wrap items-start justify-between gap-3 border-b border-line px-5 py-4">
      <div className="min-w-0">
        <h2 className="text-[15px] font-semibold text-ink">{title}</h2>
        {description && <p className="mt-0.5 text-[13px] text-ink-subtle">{description}</p>}
      </div>
      {actions && <div className="flex shrink-0 items-center gap-2">{actions}</div>}
    </header>
  )
}

/* ---------- бейджи ---------- */

type BadgeTone = 'positive' | 'negative' | 'warning' | 'neutral' | 'advice' | 'brand'

const badgeTones: Record<BadgeTone, string> = {
  positive: 'bg-positive-soft text-positive',
  negative: 'bg-negative-soft text-negative',
  warning: 'bg-warning-soft text-warning',
  neutral: 'bg-neutral-soft text-neutral-tone',
  advice: 'bg-advice-soft text-advice',
  brand: 'bg-brand-50 text-brand-700 dark:bg-brand-950 dark:text-brand-200',
}

export function Badge({
  tone = 'neutral',
  className,
  children,
}: {
  tone?: BadgeTone
  className?: string
  children: ReactNode
}) {
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium whitespace-nowrap',
        badgeTones[tone],
        className,
      )}
    >
      {children}
    </span>
  )
}

/** Пульсирующая точка «идёт запись» — тот же приём, что в старой панели. */
export function RecordingDot() {
  return (
    <span className="relative flex size-2">
      <span className="absolute inline-flex size-full animate-ping rounded-full bg-accent-500 opacity-70" />
      <span className="relative inline-flex size-2 rounded-full bg-accent-500" />
    </span>
  )
}

/* ---------- поля ---------- */

const fieldBase =
  'w-full rounded-[var(--radius-control)] border border-line bg-surface px-3 text-sm text-ink transition-colors placeholder:text-ink-subtle focus:border-brand-500 focus:outline-none disabled:opacity-60'

export function Input({ className, ...props }: ComponentProps<'input'>) {
  return <input className={cn(fieldBase, 'h-10', className)} {...props} />
}

export function Textarea({ className, ...props }: ComponentProps<'textarea'>) {
  return <textarea className={cn(fieldBase, 'py-2 leading-relaxed', className)} {...props} />
}

export function Select({ className, children, ...props }: ComponentProps<'select'>) {
  return (
    <select className={cn(fieldBase, 'h-10 cursor-pointer pr-8', className)} {...props}>
      {children}
    </select>
  )
}

export function Field({
  label,
  hint,
  error,
  htmlFor,
  children,
}: {
  label: ReactNode
  hint?: ReactNode
  error?: ReactNode
  htmlFor?: string
  children: ReactNode
}) {
  return (
    <div className="space-y-1.5">
      <label htmlFor={htmlFor} className="block text-[13px] font-medium text-ink-muted">
        {label}
      </label>
      {children}
      {hint && !error && <p className="text-xs text-ink-subtle">{hint}</p>}
      {error && (
        <p role="alert" className="text-xs text-negative">
          {error}
        </p>
      )}
    </div>
  )
}

/* ---------- состояния ---------- */

export function Skeleton({ className }: { className?: string }) {
  return <div className={cn('animate-pulse rounded bg-surface-muted', className)} />
}

export function EmptyState({
  title,
  description,
  action,
}: {
  title: string
  description?: string
  action?: ReactNode
}) {
  return (
    <div className="flex flex-col items-center justify-center gap-2 px-6 py-12 text-center">
      <p className="text-sm font-medium text-ink">{title}</p>
      {description && <p className="max-w-sm text-[13px] text-ink-subtle">{description}</p>}
      {action}
    </div>
  )
}

export function ErrorState({ message, onRetry }: { message: string; onRetry?: () => void }) {
  return (
    <div className="flex flex-col items-center justify-center gap-3 px-6 py-12 text-center">
      <p className="text-sm text-negative">{message}</p>
      {onRetry && (
        <Button size="sm" onClick={onRetry}>
          Повторить
        </Button>
      )}
    </div>
  )
}

/** Полоса-индикатор доли: соблюдение скрипта, доля времени с речью. */
export function Meter({
  value,
  tone = 'brand',
  className,
}: {
  value: number
  tone?: 'brand' | BadgeTone
  className?: string
}) {
  const fills: Record<string, string> = {
    brand: 'bg-brand-600',
    positive: 'bg-positive',
    warning: 'bg-warning',
    negative: 'bg-negative',
    neutral: 'bg-neutral-tone',
    advice: 'bg-advice',
  }
  return (
    <div
      className={cn('h-1.5 w-full overflow-hidden rounded-full bg-surface-muted', className)}
      role="presentation"
    >
      <div
        className={cn('h-full rounded-full transition-[width] duration-300', fills[tone])}
        style={{ width: `${Math.min(100, Math.max(0, value))}%` }}
      />
    </div>
  )
}

/** Порог 70/40 — тот же, что в старой панели. */
export function complianceTone(percent: number | null | undefined): BadgeTone {
  if (percent == null) return 'neutral'
  if (percent >= 70) return 'positive'
  if (percent >= 40) return 'warning'
  return 'negative'
}

export function sentimentTone(label: string | null | undefined): BadgeTone {
  if (label === 'positive') return 'positive'
  if (label === 'negative') return 'negative'
  return 'neutral'
}

export function statusTone(status: string): BadgeTone {
  if (status === 'active') return 'warning'
  if (status === 'completed') return 'positive'
  if (status === 'failed') return 'negative'
  return 'neutral'
}
